import React, { useEffect, useRef, useCallback } from "react";

import { srcPath } from "../util/Path";
import { useImgLoaded } from "../hooks/CustomHooks";

interface Props {
  grade: number;
  unit: number;
  left?: number;
  style?: any;
}

// Reference: https://my.oschina.net/codingDog/blog/1839098
const SCALE = 0.8;
const IMG_MAX_WIDTH = 600 * SCALE;
const IMG_MAX_HEIGHT = 130 * SCALE;
const AMPLITUDE = 12;
const PERIOD = 1;
const FREQUENCY = 0.8;
const FPS = 70;
const INTERVAL = 1000 / FPS;

const Logo = (props: Props) => {
  const { grade, unit, left, style } = props;
  const [imgLoaded, addImgLoaded] = useImgLoaded(2);
  const addImgLoadedCallback = useCallback(addImgLoaded, []);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let imgWidth: number, imgHeight: number;
    let canvasWidth: number, canvasHeight: number;

    let wavelength: number, velocity: number;
    let cftX: number, cftA: number;

    let stop = false;
    let timeNow = Date.now();
    let timeLast = timeNow;
    let delta = 0;
    let distance = 0;

    let onLoaded = false;

    const image = new Image();
    image.src = srcPath(`shared/logo/flags/p${grade}u${unit}.png`);

    const ctx = canvasRef.current.getContext("2d");

    const tick = () => {
      if (stop) return false;

      timeNow = Date.now();
      delta = timeNow - timeLast;

      if (delta > INTERVAL) {
        timeLast = timeNow;
        distance += (delta / 1000) * velocity;
        ctx.clearRect(0, 0, canvasWidth, canvasHeight);

        for (let x = 0; x < imgWidth; x++) {
          const sine = Math.sin(cftX * (x - distance));
          const y = cftA * (imgWidth - x) * sine + AMPLITUDE;
          ctx.drawImage(image, x, 0, 1, imgHeight, x, y, 1, imgHeight);
        }

        if (!onLoaded) {
          addImgLoadedCallback();
          onLoaded = true;
        }
      }
      requestAnimationFrame(tick);
    };

    image.onload = () => {
      setTimeout(() => {
        if (!canvasRef.current) return;
        imgWidth = Math.floor(image.width);
        imgHeight = Math.floor(image.height);

        let scale = 1;
        if (imgWidth > IMG_MAX_WIDTH) {
          scale = IMG_MAX_WIDTH / imgWidth;
        }
        if (imgHeight > IMG_MAX_HEIGHT) {
          scale = (scale * IMG_MAX_HEIGHT) / imgHeight;
        }

        canvasWidth = imgWidth;
        canvasHeight = imgHeight + AMPLITUDE * 2;

        canvasRef.current.width = canvasWidth;
        canvasRef.current.height = canvasHeight;
        canvasRef.current.style.transform = `translate3d(-50%,-50%,0) scale(${scale})`;

        wavelength = imgWidth / PERIOD;
        cftX = (2 * Math.PI) / wavelength;
        cftA = AMPLITUDE / imgWidth;
        velocity = wavelength * FREQUENCY;

        tick();
      }, 10);
    };
  }, [addImgLoadedCallback, grade, unit]);

  return (
    <div
      className="abs logo"
      style={{
        left: `${(left || 0) + 250}px`,
        display: imgLoaded ? "block" : "none",
        ...style,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{ display: imgLoaded ? "block" : "none" }}
      />
      <img
        className="logo-plane"
        src={srcPath(`shared/logo/p${props.grade}.png`)}
        alt=""
        onLoad={addImgLoaded}
      />
    </div>
  );
};

export default Logo;
